import {
  folderListResultSchema,
  jobSchema,
  uploadResultSchema,
  type FolderEntry,
  type Job,
  type MoveRequest,
  type UploadResult,
} from '@xphoto/shared';
import { ApiError } from '../gallery/api';
import { NO_TIMEOUT, apiFetch } from '../lib/http';

/**
 * Client typé de l'ORGANISATION physique des médias (LOT 2 — déplacement de fichiers).
 * Comme les autres clients, la réponse est RE-PARSÉE via son schéma partagé
 * `@xphoto/shared` (« never trust the client », y compris ses propres réponses).
 */

async function readJson(res: Response): Promise<unknown> {
  if (!res.ok) {
    let code = 'UNKNOWN';
    try {
      const body = (await res.json()) as { error?: { code?: string } };
      if (typeof body?.error?.code === 'string') code = body.error.code;
    } catch {
      // Corps non-JSON : on garde le code générique.
    }
    throw new ApiError(res.status, code);
  }
  return res.json();
}

/**
 * `POST /api/libraries/:id/move` — enfile un job de déplacement (LOT 2). Le corps
 * cible SOIT une sélection d'`assetIds`, SOIT toutes les vidéos avec son
 * (`hasAudio: true`). Renvoie le job créé (201). Une écriture désactivée renvoie
 * 409 `WRITE_DISABLED` → propagée en `ApiError` (statut 409, code WRITE_DISABLED).
 */
export async function enqueueMove(libraryId: string, body: MoveRequest): Promise<Job> {
  const res = await apiFetch(`/api/libraries/${libraryId}/move`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return jobSchema.parse(await readJson(res));
}

/**
 * `GET /api/libraries/:id/all-folders` — TOUS les dossiers à fichiers de la
 * bibliothèque (tout niveau) + compte direct (LOT 5). Réponse RE-PARSÉE via son
 * schéma partagé. Lève une `ApiError` sur échec HTTP.
 */
export async function fetchAllFolders(libraryId: string): Promise<FolderEntry[]> {
  const res = await apiFetch(`/api/libraries/${libraryId}/all-folders`);
  const parsed = folderListResultSchema.parse(await readJson(res));
  return parsed.folders;
}

/**
 * `POST /api/libraries/:id/upload` (multipart/form-data) — TÉLÉVERSE des fichiers dans
 * un dossier de la bibliothèque, puis le serveur enfile un job `scan` pour les indexer
 * (XPHOTO-16). Réponse RE-PARSÉE via son schéma partagé (`uploadResultSchema`).
 *
 * ⚠ FormData : on N'AJOUTE PAS d'en-tête `Content-Type` — le navigateur pose lui-même
 * le boundary multipart (`apiFetch` ne fixe aucun Content-Type par défaut, seul l'en-tête
 * d'authentification `X-Active-Slot` est joint). Une écriture désactivée renvoie 409
 * `WRITE_DISABLED` → propagée en `ApiError` (statut 409, code WRITE_DISABLED).
 *
 * ⚠ AUCUN plafond de durée (`NO_TIMEOUT`) : la durée d'un téléversement dépend de la
 * TAILLE des fichiers (plusieurs Go) et du débit montant de l'utilisateur — aucune
 * borne fixe n'est défendable, et couper un upload à 95 % serait bien pire que le
 * chargement perpétuel que le plafond par défaut évite ailleurs.
 */
export async function uploadFiles(
  libraryId: string,
  destFolder: string,
  files: readonly File[],
): Promise<UploadResult> {
  const form = new FormData();
  // Le dossier de destination DOIT précéder les fichiers (le serveur streame au fil de l'eau).
  form.append('destFolder', destFolder);
  for (const file of files) {
    form.append('files', file, file.name);
  }
  const res = await apiFetch(`/api/libraries/${libraryId}/upload`, {
    method: 'POST',
    body: form,
    timeoutMs: NO_TIMEOUT,
  });
  return uploadResultSchema.parse(await readJson(res));
}
